import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMediaQuery, useTheme } from '@mui/material';

import { CacheKey } from '../../utils/common/constants';

import { SidePanelContext, SidePanelContextValue, SidePanelRegistration } from './context';
import {
  ContentRow,
  MainContentPanel,
  ResizeHandle,
  SideContentPanel,
  SideDrawer,
  SideScroll,
} from './index.styles';

const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const MIN_MAIN_WIDTH = 360;
const GAP = 16;
const SAVE_DEBOUNCE_MS = 500;

const widthCacheKey = (id: string) => `${CacheKey.SidePanelWidth}:${id}`;

const clampWidth = (width: number, maxByContainer: number) =>
  Math.max(MIN_WIDTH, Math.min(width, Math.min(MAX_WIDTH, maxByContainer)));

interface SidePanelProviderProps {
  children: ReactNode;
}

/**
 * Owns the side-panel chrome at the PageLayout level: lays the main content
 * and (on lg+) the resize handle + side panel out as sibling rounded panels,
 * and falls back to an overlay drawer below lg. Pages register content via
 * <SidePanel> / useSidePanel.
 */
const SidePanelProvider = ({ children }: SidePanelProviderProps) => {
  const theme = useTheme();
  // Match PageLayout's layout switch: square, flush content panel below `sm`,
  // floating rounded panel at `sm` and wider.
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [registration, setRegistration] = useState<SidePanelRegistration | null>(null);
  const [slotElement, setSlotElement] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(MIN_WIDTH);
  // mounted keeps the panel in the DOM through the close transition; shown drives
  // the open/closed width so it animates in and out; resizing suspends the width
  // transition during a drag so the handle tracks the pointer instantly.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [resizing, setResizing] = useState(false);

  const rowRef = useRef<HTMLDivElement | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const rafRef = useRef<number>();

  const open = !!registration;
  const transitionMs = theme.transitions.duration.standard;

  const register = useCallback((next: SidePanelRegistration) => {
    setRegistration(prev =>
      prev &&
      prev.id === next.id &&
      prev.defaultWidth === next.defaultWidth &&
      prev.resizable === next.resizable
        ? prev
        : next
    );
  }, []);

  const unregister = useCallback((id: string) => {
    setRegistration(prev => (prev?.id === id ? null : prev));
  }, []);

  // Load the width whenever the active panel changes: a resizable panel
  // restores its cached width, a fixed one always opens at its default.
  useEffect(() => {
    if (!registration) {
      return;
    }
    const cached = Number(localStorage.getItem(widthCacheKey(registration.id)));
    setWidth(registration.resizable && cached > 0 ? cached : registration.defaultWidth);
  }, [registration]);

  // Slide in on open, slide out then unmount on close (matching the left nav).
  useEffect(() => {
    if (open) {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
      setMounted(true);
      // Two frames so the panel paints at width 0 before transitioning to width.
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }

    setShown(false);
    closeTimer.current = setTimeout(() => setMounted(false), transitionMs);
    return () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
    };
  }, [open, transitionMs]);

  const persistWidth = useCallback((id: string, value: number) => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    saveTimer.current = setTimeout(() => {
      localStorage.setItem(widthCacheKey(id), String(Math.round(value)));
    }, SAVE_DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
      }
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    []
  );

  const startResize = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const activeId = registration?.id;
      if (!activeId) {
        return;
      }

      const handleMove = (moveEvent: PointerEvent) => {
        const row = rowRef.current;
        if (!row) {
          return;
        }
        const rect = row.getBoundingClientRect();
        // Side panel right edge sits inside the retained right margin.
        const rightEdge = rect.right - GAP;
        const rawWidth = rightEdge - moveEvent.clientX;
        const maxByContainer = rect.width - GAP - GAP - MIN_MAIN_WIDTH;
        const next = clampWidth(rawWidth, maxByContainer);
        setWidth(next);
        persistWidth(activeId, next);
      };

      const handleUp = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        document.body.style.removeProperty('cursor');
        document.body.style.removeProperty('user-select');
        setResizing(false);
      };

      setResizing(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [registration, persistWidth]
  );

  // The inline panel is drawn exactly while this holds, so the flag cannot drift from it.
  const panelTakesRoom = mounted && isLg;

  const contextValue = useMemo<SidePanelContextValue>(
    () => ({ register, unregister, slotElement, panelTakesRoom }),
    [register, unregister, slotElement, panelTakesRoom]
  );

  const resizable = registration?.resizable ?? false;

  return (
    <SidePanelContext.Provider value={contextValue}>
      <ContentRow ref={rowRef} $isSm={isSm}>
        <MainContentPanel $isSm={isSm}>{children}</MainContentPanel>
        {panelTakesRoom && (
          <>
            <ResizeHandle
              $shown={shown}
              $resizable={resizable}
              onPointerDown={resizable ? startResize : undefined}
            />
            <SideContentPanel $resizing={resizing} style={{ width: shown ? width : 0 }}>
              <SideScroll ref={setSlotElement} />
            </SideContentPanel>
          </>
        )}
      </ContentRow>
      {!isLg && (
        <SideDrawer
          anchor='right'
          variant='temporary'
          open={open}
          onClose={() => setRegistration(null)}
        >
          <div ref={setSlotElement} />
        </SideDrawer>
      )}
    </SidePanelContext.Provider>
  );
};

export default SidePanelProvider;
